import React, {
    useEffect
} from "react";

import {
    useForm
} from "react-hook-form";

import {
    yupResolver
} from "@hookform/resolvers/yup";

import * as Yup from "yup";

import {
    useCreateQASectionMutation,
    useUpdateQASectionMutation
} from "./Api";

import ToastrService from "../../services/toastService";
import Loader from "../loader/loader";

const AddQASection = ({
    mode = "add",
    isReadOnly = false,
    onClose,
    onSave,
    data = null
}) => {

    /*
     * Create section API
     */
    const [
        createQASectionApi,
        {
            isLoading: createLoading
        }
    ] = useCreateQASectionMutation();

    /*
     * Update section API
     */
    const [
        updateQASectionApi,
        {
            isLoading: updateLoading
        }
    ] = useUpdateQASectionMutation();

    const isEditMode =
        mode === "edit";

    const isViewMode =
        mode === "view" ||
        isReadOnly;

    const isSubmitting =
        createLoading ||
        updateLoading;

    /*
     * Validation
     */
    const validationSchema =
        Yup.object().shape({

            sectionName:
                Yup.string()
                    .transform(
                        (value) =>
                            value?.trim()
                    )
                    .required(
                        "Section name is required"
                    )
                    .matches(
                        /^[A-Za-z0-9 &(),./'-]+$/,
                        "Enter a valid section name"
                    )
                    .min(
                        3,
                        "Section name must contain at least 3 characters"
                    )
                    .max(
                        100,
                        "Section name cannot exceed 100 characters"
                    ),
            sectionType: Yup.string()
                .required(
                    "Section type is required"
                )
                .oneOf(
                    [
                        "normal",
                        "critical_violation"
                    ],
                    "Select a valid section type"
                ),
            weightage: Yup.number()
                .transform(
                    (
                        value,
                        originalValue
                    ) =>
                        originalValue === ""
                            ? undefined
                            : value
                )
                .when(
                    "sectionType",
                    {
                        is: "critical_violation",

                        then: (
                            schema
                        ) =>
                            schema
                                .typeError(
                                    "Weightage must be 0"
                                )
                                .required(
                                    "Weightage is required"
                                )
                                .oneOf(
                                    [0],
                                    "Critical violation section weightage must be 0"
                                ),

                        otherwise: (
                            schema
                        ) =>
                            schema
                                .typeError(
                                    "Weightage is required"
                                )
                                .required(
                                    "Weightage is required"
                                )
                                .min(
                                    1,
                                    "Weightage must be at least 1%"
                                )
                                .max(
                                    100,
                                    "Weightage cannot exceed 100%"
                                )
                    }
                ),
            // weightage:
            //     Yup.number()
            //         .transform(
            //             (
            //                 value,
            //                 originalValue
            //             ) =>
            //                 originalValue === ""
            //                     ? undefined
            //                     : value
            //         )
            //         .typeError(
            //             "Weightage is required"
            //         )
            //         .required(
            //             "Weightage is required"
            //         )
            //         .min(
            //             1,
            //             "Weightage must be at least 1%"
            //         )
            //         .max(
            //             100,
            //             "Weightage cannot exceed 100%"
            //         ),

            displayOrder:
                Yup.number()
                    .transform(
                        (
                            value,
                            originalValue
                        ) =>
                            originalValue === ""
                                ? undefined
                                : value
                    )
                    .typeError(
                        "Display order is required"
                    )
                    .required(
                        "Display order is required"
                    )
                    .integer(
                        "Display order must be a whole number"
                    )
                    .min(
                        1,
                        "Display order must be at least 1"
                    ),

            status:
                Yup.string()
                    .required(
                        "Status is required"
                    )
                    .oneOf(
                        [
                            "active",
                            "inactive"
                        ],
                        "Select a valid status"
                    ),

            isMandatory:
                Yup.boolean(),

            includeInScore:
                Yup.boolean()

        });

    const defaultFormValues = {
        sectionName: "",
        sectionType: "normal",
        weightage: "",
        displayOrder: "",
        status: "active",
        isMandatory: false,
        includeInScore: true
    };

    const {
        register,
        handleSubmit,
        reset,
        formState: {
            errors
        }
    } = useForm({

        resolver:
            yupResolver(
                validationSchema
            ),

        mode: "all",

        defaultValues:
            defaultFormValues

    });

    /*
     * Fill form for edit or view.
     *
     * Clear form for add.
     */
    useEffect(() => {

        if (
            (
                isEditMode ||
                isViewMode
            ) &&
            data
        ) {

            reset({
                sectionName:
                    data.section_name || "",
                sectionType:
                    data.section_type ?? "",
                weightage:
                    data.weightage ?? "",

                displayOrder:
                    data.display_order ?? "",

                status:
                    data.status || "active",

                isMandatory:
                    Boolean(
                        data.is_mandatory
                    ),

                includeInScore:
                    data.include_in_score ===
                        undefined
                        ? true
                        : Boolean(
                            data.include_in_score
                        )
            });

            return;

        }

        reset(
            defaultFormValues
        );

    }, [
        data,
        isEditMode,
        isViewMode,
        reset
    ]);

    /*
     * Reset form
     */
    const resetButton = () => {

        if (
            isEditMode &&
            data
        ) {

            reset({
                sectionName:
                    data.section_name || "",
                sectionType:
                    data.section_type || "",
                weightage:
                    data.weightage ?? "",

                displayOrder:
                    data.display_order ?? "",

                status:
                    data.status || "active",

                isMandatory:
                    Boolean(
                        data.is_mandatory
                    ),

                includeInScore:
                    data.include_in_score ===
                        undefined
                        ? true
                        : Boolean(
                            data.include_in_score
                        )
            });

            return;

        }

        reset(
            defaultFormValues
        );

    };

    /*
     * RTK Query error message
     */
    const getErrorMessage = (
        response,
        defaultMessage
    ) => {

        return (
            response?.error
                ?.data
                ?.message ||

            response?.data
                ?.message ||

            defaultMessage
        );

    };

    /*
     * Add or update submit
     */
    const onSubmit = async (
        values
    ) => {

        try {

            const payload = {

                section_name:
                    values
                        .sectionName
                        .trim(),
                section_type:
                    values
                        .sectionType
                        .trim(),
                weightage:
                    Number(
                        values.weightage
                    ),

                display_order:
                    Number(
                        values.displayOrder
                    ),

                status:
                    values.status,

                is_mandatory:
                    values.isMandatory
                        ? 1
                        : 0,

                include_in_score:
                    values.includeInScore
                        ? 1
                        : 0

            };

            let response;

            /*
             * Edit section
             */
            if (isEditMode) {

                if (!data?.id) {

                    ToastrService.error(
                        "Section ID is required"
                    );

                    return;

                }

                response =
                    await updateQASectionApi({
                        id: data.id,
                        ...payload
                    });

            } else {

                /*
                 * Create section
                 */
                response =
                    await createQASectionApi(
                        payload
                    );

            }

            if (
                response?.data?.success
            ) {

                ToastrService.success(
                    response?.data?.message ||
                    (
                        isEditMode
                            ? "QA section updated successfully"
                            : "QA section created successfully"
                    )
                );

                reset(
                    defaultFormValues
                );

                onSave?.(
                    response?.data?.data
                );

                return;

            }

            ToastrService.error(
                getErrorMessage(
                    response,
                    isEditMode
                        ? "Unable to update QA section"
                        : "Unable to create QA section"
                )
            );

        } catch (error) {

            console.error(
                "QA section submit error:",
                error
            );

            ToastrService.error(
                error?.data?.message ||
                error?.message ||
                "Something went wrong. Please try again."
            );

        }

    };

    /*
     * Drawer title
     */
    const getDrawerTitle = () => {

        if (isViewMode) {
            return "View QA Section";
        }

        if (isEditMode) {
            return "Edit QA Section";
        }

        return "Create QA Section";

    };

    /*
     * Drawer description
     */
    const getDrawerDescription = () => {

        if (isViewMode) {

            return "View the selected evaluation section details.";

        }

        if (isEditMode) {

            return "Update the selected evaluation section details.";

        }

        return "Fill in the details below to create a new evaluation section.";

    };

    return (
        <div>

            {
                isSubmitting && (
                    <Loader />
                )
            }

            <div
                className="offcanvas offcanvas-end show"
                style={{
                    visibility:
                        "visible"
                }}
                tabIndex="-1"
                aria-labelledby="qaSectionDrawer"
            >

                {/* Header */}

                <div className="offcanvas-header px-4 py-3">

                    <div>

                        <h3
                            id="qaSectionDrawer"
                            className="mb-1 drawer-header"
                        >
                            {
                                getDrawerTitle()
                            }
                        </h3>

                        <small className="text-muted">

                            {
                                getDrawerDescription()
                            }

                        </small>

                    </div>

                    <button
                        type="button"
                        className="btn-close text-reset"
                        aria-label="Close"
                        disabled={
                            isSubmitting
                        }
                        onClick={
                            onClose
                        }
                    />

                </div>

                {/* Body */}

                <div className="offcanvas-body px-4 py-3">

                    <form
                        autoComplete="off"
                        noValidate
                        onSubmit={
                            handleSubmit(
                                onSubmit
                            )
                        }
                    >

                        {/* Section Name */}

                        <div className="mb-3">

                            <label
                                htmlFor="sectionName"
                                className="form-label"
                            >
                                Section Name

                                <span className="text-danger ms-1">
                                    *
                                </span>
                            </label>

                            <input
                                type="text"
                                id="sectionName"
                                placeholder="Enter section name"
                                className={`form-control ${errors.sectionName
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                disabled={
                                    isViewMode ||
                                    isSubmitting
                                }
                                {...register(
                                    "sectionName"
                                )}
                            />

                            {
                                errors.sectionName && (
                                    <div className="invalid-feedback">

                                        {
                                            errors
                                                .sectionName
                                                .message
                                        }

                                    </div>
                                )
                            }

                        </div>

                        {/* {Section Type}  */}
                        <div className="mb-3">
                            <label
                                htmlFor="sectionType"
                                className="form-label"
                            >
                                Section Type

                                <span className="text-danger ms-1">
                                    *
                                </span>
                            </label>

                            <select
                                id="sectionType"
                                className={`form-select ${errors.sectionType
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                {...register("sectionType")}
                            >
                                <option value="normal">
                                    Normal Scoring Section
                                </option>

                                <option value="critical_violation">
                                    Critical Violation / Auto Fail
                                </option>
                            </select>

                            {errors.sectionType && (
                                <div className="invalid-feedback">
                                    {errors.sectionType.message}
                                </div>
                            )}
                        </div>

                        {/* Weightage and Order */}

                        <div className="row">

                            <div className="col-md-6">

                                <div className="mb-3">

                                    <label
                                        htmlFor="weightage"
                                        className="form-label"
                                    >
                                        Weightage (%)

                                        <span className="text-danger ms-1">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="number"
                                        id="weightage"
                                        min="1"
                                        max="100"
                                        step="1"
                                        placeholder="Example: 20"
                                        className={`form-control ${errors.weightage
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        disabled={
                                            isViewMode ||
                                            isSubmitting
                                        }
                                        {...register(
                                            "weightage"
                                        )}
                                    />

                                    {
                                        errors.weightage && (
                                            <div className="invalid-feedback">

                                                {
                                                    errors
                                                        .weightage
                                                        .message
                                                }

                                            </div>
                                        )
                                    }

                                </div>

                            </div>

                            <div className="col-md-6">

                                <div className="mb-3">

                                    <label
                                        htmlFor="displayOrder"
                                        className="form-label"
                                    >
                                        Display Order

                                        <span className="text-danger ms-1">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="number"
                                        id="displayOrder"
                                        min="1"
                                        step="1"
                                        placeholder="Example: 1"
                                        className={`form-control ${errors.displayOrder
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        disabled={
                                            isViewMode ||
                                            isSubmitting
                                        }
                                        {...register(
                                            "displayOrder"
                                        )}
                                    />

                                    {
                                        errors.displayOrder && (
                                            <div className="invalid-feedback">

                                                {
                                                    errors
                                                        .displayOrder
                                                        .message
                                                }

                                            </div>
                                        )
                                    }

                                </div>

                            </div>

                        </div>

                        {/* Status */}

                        <div className="mb-3">

                            <label
                                htmlFor="status"
                                className="form-label"
                            >
                                Status

                                <span className="text-danger ms-1">
                                    *
                                </span>
                            </label>

                            <select
                                id="status"
                                className={`form-select ${errors.status
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                disabled={
                                    isViewMode ||
                                    isSubmitting
                                }
                                {...register(
                                    "status"
                                )}
                            >
                                <option value="active">
                                    Active
                                </option>

                                <option value="inactive">
                                    Inactive
                                </option>

                            </select>

                            {
                                errors.status && (
                                    <div className="invalid-feedback">

                                        {
                                            errors
                                                .status
                                                .message
                                        }

                                    </div>
                                )
                            }

                        </div>

                        {/* Mandatory */}

                        <div className="mb-3">

                            <div className="form-check">

                                <input
                                    type="checkbox"
                                    id="isMandatory"
                                    className="form-check-input"
                                    disabled={
                                        isViewMode ||
                                        isSubmitting
                                    }
                                    {...register(
                                        "isMandatory"
                                    )}
                                />

                                <label
                                    htmlFor="isMandatory"
                                    className="form-check-label custom-checkbox-label"
                                >
                                    Mandatory Section
                                </label>

                            </div>

                            <small className="text-muted">

                                The evaluator must complete all mandatory questions in this section.

                            </small>

                        </div>

                        {/* Include score */}

                        <div className="mb-3">

                            <div className="form-check">

                                <input
                                    type="checkbox"
                                    id="includeInScore"
                                    className="form-check-input"
                                    disabled={
                                        isViewMode ||
                                        isSubmitting
                                    }
                                    {...register(
                                        "includeInScore"
                                    )}
                                />

                                <label
                                    htmlFor="includeInScore"
                                    className="form-check-label custom-checkbox-label"
                                >
                                    Include in Overall Score
                                </label>

                            </div>

                            <small className="text-muted">

                                Section score will be included in the final QA evaluation score.

                            </small>

                        </div>

                        {/* Help */}

                        <div className="alert alert-light border mb-3">

                            <div className="small">

                                <strong>
                                    Score configuration:
                                </strong>

                                <div className="mt-1">

                                    The total weightage of all active evaluation sections should equal 100%.

                                </div>

                                <div>

                                    Questions can be added under this section after the section is created.

                                </div>

                            </div>

                        </div>

                        {/* Actions */}

                        <div className="d-flex flex-wrap gap-2 mt-4">

                            {
                                !isViewMode && (
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={
                                            isSubmitting
                                        }
                                    >
                                        {
                                            isSubmitting
                                                ? (
                                                    isEditMode
                                                        ? "Updating..."
                                                        : "Creating..."
                                                )
                                                : (
                                                    isEditMode
                                                        ? "Update Section"
                                                        : "Create Section"
                                                )
                                        }
                                    </button>
                                )
                            }

                            {
                                !isViewMode && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        disabled={
                                            isSubmitting
                                        }
                                        onClick={
                                            resetButton
                                        }
                                    >
                                        {
                                            isEditMode
                                                ? "Reset Changes"
                                                : "Reset"
                                        }
                                    </button>
                                )
                            }

                            <button
                                type="button"
                                className="btn btn-light"
                                disabled={
                                    isSubmitting
                                }
                                onClick={
                                    onClose
                                }
                            >
                                {
                                    isViewMode
                                        ? "Close"
                                        : "Cancel"
                                }
                            </button>

                        </div>

                    </form>

                </div>

            </div>

            {/* Backdrop */}

            <div
                className="offcanvas-backdrop fade show"
                onClick={
                    isSubmitting
                        ? undefined
                        : onClose
                }
            />

        </div>
    );

};

export default AddQASection;